import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

/** Merge class names, letting later Tailwind classes win over earlier ones.
 *  Without twMerge, a variant's `px-4` and an override's `px-2` both land in
 *  the class list and the winner is whatever the stylesheet ordered. */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const usd = (cents: number) => `$${(cents / 100).toFixed(2)}`

/** Relative time, for run logs and activity. "2m ago" is easier to judge than
 *  a timestamp when you are watching something happen. */
export function ago(iso: string): string {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000)
  if (s < 60) return `${Math.floor(s)}s ago`
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return `${Math.floor(s / 86400)}d ago`
}

/** A provider model ID is useful for APIs but noisy in a compact product UI. */
export function modelName(id: string | null | undefined): string {
  if (!id) return ''
  const name = id.split('/').at(-1) ?? id
  return name
    .split('-')
    .map(part => /^\d+$/.test(part) ? part : part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export const wordCount = (t: string) => (t.trim() ? t.trim().split(/\s+/).length : 0)

/** Bytes in the units a person reads them in. Decimal rather than binary,
 *  because the file manager the user got the number from uses decimal too. */
export function bytes(n: number): string {
  if (n < 1000) return `${n} B`
  const units = ['KB', 'MB', 'GB']
  let v = n / 1000
  let i = 0
  while (v >= 1000 && i < units.length - 1) { v /= 1000; i++ }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`
}

/** Coarse on purpose: a countdown that ticks every second invites watching it,
 *  and an estimate from a variable connection does not deserve that precision. */
export function duration(seconds: number): string {
  if (seconds < 5) return 'a moment'
  if (seconds < 60) return `${Math.round(seconds / 5) * 5}s`
  const m = Math.round(seconds / 60)
  return m < 60 ? `${m} min` : `${Math.round(m / 60)} h`
}

/** Exact media length, unlike the coarse estimates used for task progress. */
export function mediaDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return 'Duration unavailable'
  const total = Math.round(seconds)
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor(total / 60) % 60
  const remaining = String(total % 60).padStart(2, '0')
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${remaining}`
    : `${minutes}:${remaining}`
}
